import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Code, Zap, Bot, Shield, ChevronUp, Calendar } from 'lucide-react';
import Footer from '../components/Footer';
import ScrollIndicator from '../components/ScrollIndicator';

const Divisions = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setShowScrollTop(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div ref={containerRef} className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-container">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white min-h-[90vh] snap-start flex flex-col justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-slate-900/0"></div>
        <div className="container-width relative flex-1 flex flex-col justify-center items-center py-16 sm:py-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">Our Divisions</h1>
            <div className="space-y-4">
              <p className="text-base sm:text-lg text-slate-300">
We split our team into specialized divisions because, let’s be honest, making one team handle everything would be a glorified meltdown waiting to happen. Each division is laser-focused on what they do best—whether it’s coding high-performance algo strategies, designing sleek UIs, or making sure automation doesn’t turn into an expensive guessing game.
              </p>
              <p className="text-base sm:text-lg text-slate-300">
This setup ensures expertise, efficiency, and fewer all-nighters (for us, not you). So, whether you need an automation software, a custom algo, or some “this-shouldn’t-be-possible-but-let’s-try-it-anyway” solution, we’ve got a team for that.
              </p>
            </div>
          </div>
        </div>
        <ScrollIndicator text="Explore Our Divisions" />
      </section>

      {/* ViSiON Section */}
      <section className="min-h-screen snap-start bg-slate-50 flex items-center py-24">
        <div className="container-width">
          <div className="bg-white rounded-xl shadow-lg p-8 sm:p-10">
            <div className="flex items-center mb-8">
              <Zap className="w-12 h-12 text-blue-500 mr-4" />
              <h2 className="text-3xl font-bold">ViSiON</h2>
            </div>
            <h3 className="text-xl font-semibold mb-8">Algorithmic Trading & Backtesting Solutions</h3>
            <div className="prose max-w-none text-slate-600">
              <p className="mb-6">
                ViSiON represents clarity, precision, and strategic execution—the core principles of algorithmic trading. As financial markets become increasingly volatile, speed and accuracy are non-negotiable. ViSiON is dedicated to building custom trading algorithms that execute trades seamlessly, minimizing human errors and maximizing opportunities.
              </p>
              <p className="mb-6">
                Beyond development, we offer comprehensive backtesting services, allowing traders to refine and validate their strategies against historical data before live deployment. Whether you're a retail trader or a financial institution, ViSiON ensures your strategies are rigorously tested and executed with the highest level of accuracy and reliability.
              </p>
              <p>
                To maintain complete confidentiality, every project is protected under NDAs and formal ownership agreements, ensuring that your trading strategies remain exclusively yours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ViSFLOW Section */}
      <section className="min-h-screen snap-start bg-white flex items-center py-24">
        <div className="container-width">
          <div className="bg-slate-50 rounded-xl shadow-lg p-8 sm:p-10">
            <div className="flex items-center mb-8">
              <Bot className="w-12 h-12 text-blue-500 mr-4" />
              <h2 className="text-3xl font-bold">ViSFLOW</h2>
            </div>
            <h3 className="text-xl font-semibold mb-8">Process Automation & Chatbot Development</h3>
            <div className="prose max-w-none text-slate-600">
              <p className="mb-8">
                In today's fast-moving digital landscape, efficiency is key. ViSFLOW represents effortless automation and the smooth flow of business operations, helping businesses integrate intelligent solutions without unnecessary complexity.
              </p>
              <h4 className="text-lg font-semibold mb-6">ViSFLOW specializes in:</h4>
              <ul className="space-y-6">
                <li className="flex items-start">
                  <Code className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                  <span>Custom Python automation tools to eliminate repetitive tasks and reduce manual errors</span>
                </li>
                <li className="flex items-start">
                  <Bot className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                  <span>Telegram and Discord chatbots for customer engagement, support, and community management</span>
                </li>
                <li className="flex items-start">
                  <Shield className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                  <span>Game bots for Discord, designed to enhance interactive experiences and automate in-game functionalities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Security & CTA Section */}
      <section className="snap-start">
        <div className="bg-slate-50 py-24">
          <div className="container-width">
            <div className="bg-white rounded-xl shadow-lg p-8 sm:p-10">
              <div className="flex items-center mb-8">
                <Shield className="w-12 h-12 text-blue-500 mr-4" />
                <h2 className="text-3xl font-bold">Security & Confidentiality</h2>
              </div>
              <div className="prose max-w-none text-slate-600">
                <p className="mb-8">
                  For every algorithmic project we undertake, we prioritize the security and confidentiality of your strategies. Our processes are transparent, and we ensure that our clients retain full ownership and control of their proprietary code.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Security Measures</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Non-Disclosure Agreements (NDAs)
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Formal Ownership Acknowledgments
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Secure Development Environment
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Client Benefits</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Full Ownership of Solutions
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Protected Intellectual Property
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        Exclusive Rights to Strategies
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-500 text-white py-16">
          <div className="container-width text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl mb-8">Contact us to discuss your project requirements</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="space-y-4">
                <p className="text-lg">Email: <a href="mailto:contact@visrupt.in" className="underline">contact@visrupt.in</a></p>
                <p className="text-lg">Phone: <a href="tel:+917522979568" className="underline">+91-7522979568</a></p>
              </div>
              <div className="sm:ml-8">
                <a
                  href="https://calendly.com/visrupt/schedule-a-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-500 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 group"
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </section>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Divisions;